import React, { useState } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { LOCALES, formatCount } from "../charts/format";
import DataTable from "../charts/DataTable";
import { ExternalLink } from "lucide-react";
import InlineBar from "./InlineBar";
import Section from "./Section";

const SHOWN = 10;
const secondary = { color: "var(--color-text-secondary)" };
const card = { borderColor: "var(--color-border)", backgroundColor: "var(--color-surface)" };

// A miniatura de um cartao: um `video` sem som para o que e video, um `img` para o resto, e nada sem
// `media_url` (nem um `img` partido). E so desenho: o titulo ao lado diz o que e.
const Thumbnail = ({ url, type }) => {
  if (!url) return null;
  const size = "w-10 h-10 rounded object-cover shrink-0";
  return type === "video" ? (
    <video src={url} muted playsInline preload="metadata" aria-hidden="true" tabIndex={-1} className={size} />
  ) : (
    <img src={url} alt="" loading="lazy" width={40} height={40} className={size} />
  );
};

// O titulo e uma ligacao para um separador novo quando ha `link_url`, e texto simples sem ela.
const CardName = ({ title, media, link, children }) => (
  <span className="inline-flex flex-wrap items-center gap-2">
    <Thumbnail {...media} />
    {link ? (
      <a href={link} target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">
        {title}
        <ExternalLink size={12} aria-hidden="true" className="shrink-0" />
      </a>
    ) : (
      <span>{title}</span>
    )}
    {children}
  </span>
);

// Uma tabela de cartoes, as dez primeiras linhas e o resto a pedido, como as perguntas do Conteudo.
const CardsTable = ({ caption, columns, rows, language }) => {
  const { translate } = useLanguage();
  const [all, setAll] = useState(false);
  return (
    <div className="rounded-xl border p-4 space-y-3" style={card}>
      <DataTable caption={caption} columns={columns} rows={all ? rows : rows.slice(0, SHOWN)} />
      {rows.length > SHOWN && (
        <button type="button" onClick={() => setAll((shown) => !shown)} className="text-xs underline" style={secondary}>
          {all ? translate("analyticsShowFewerCards") : translate("analyticsShowAllCards", { count: formatCount(rows.length, language) })}
        </button>
      )}
    </div>
  );
};

// Desde quando os cliques contam (D11): os instantes comparam-se (`period.start` da resposta contra o
// `clicks_since`), e nunca o dia `from`, que deixava de dizer "desde" quando o primeiro clique caia no
// primeiro dia a meio da manha. Sem `clicks_since` o chat ainda nao registou cliques.
const clicksNote = (activity, translate, language, cart) => {
  const since = activity.clicks_since;
  if (!since) return translate("analyticsClicksNotRecorded");
  if (new Date(since) <= new Date(activity.period.start)) return null;
  const date = new Intl.DateTimeFormat(LOCALES[language] || language, { dateStyle: "medium", timeStyle: "short", timeZone: activity.period.tz }).format(new Date(since));
  return translate(cart ? "analyticsClicksSince" : "analyticsClicksSinceNoCart", { date });
};

// Os cartoes mais mostrados (D11 do plano do RPAA-286): os produtos e os curados, cada lista so com itens.
// Os titulos dos curados (nomes de fits da Salsa) e o ambito desenham-se como vem da config, sem passar
// pelo `translate` (o `t()` devolve a chave que nao conhece, e um teste com o titulo nao o apanhava).
const CardsShown = ({ activity, cart }) => {
  const { translate, language } = useLanguage();
  const { products, curated } = activity;
  const note = clicksNote(activity, translate, language, cart);
  const shownNote = translate(cart ? "analyticsCardsShownNote" : "analyticsCardsShownNoteNoCart");
  const productMax = Math.max(0, ...products.items.map((item) => item.shown));
  const curatedMax = Math.max(0, ...curated.items.map((item) => item.shown));
  const nothing = products.items.length === 0 && curated.items.length === 0;
  const count = (value) => formatCount(value, language);
  const clicksNoteView = note && (
    <p className="text-xs" style={secondary}>
      {note}
    </p>
  );
  const numeric = (key, label) => ({ key, label, numeric: true });
  const topCurated = curated.items[0];
  // A coluna do ambito so existe quando algum curado a tem.
  const hasScope = curated.items.some((item) => item.scope);

  return (
    <>
      {nothing && (
        <Section title={translate("analyticsTopCards")} className="space-y-2">
          <p className="text-sm" style={secondary}>
            {translate("analyticsNoCardsShown")}
          </p>
        </Section>
      )}
      {products.items.length > 0 && (
        <Section
          title={translate("analyticsTopProducts")}
          note={products.distinct === 0 ? null : translate(products.distinct === 1 ? "analyticsTopProductsLeadOne" : "analyticsTopProductsLead", { products: count(products.distinct) })}
          info={shownNote}
          className="space-y-2"
        >
          {clicksNoteView}
          {products.distinct > products.items.length && (
            <p className="text-xs" style={secondary}>
              {translate("analyticsTopProductsLimited", { count: count(products.items.length) })}
            </p>
          )}
          <CardsTable
            caption={translate("analyticsTopProducts")}
            language={language}
            columns={[
              { key: "product", label: translate("analyticsProduct") },
              numeric("shown", translate("analyticsShownColumn")),
              numeric("clicks", translate("analyticsClicksColumn")),
              ...(cart ? [numeric("cart", translate("analyticsCartColumn"))] : []),
            ]}
            rows={products.items.map((item) => ({
              key: item.id,
              product: <CardName title={item.title || item.id} media={{ url: item.media_url, type: item.media_type }} link={item.link_url} />,
              shown: <InlineBar value={item.shown} max={productMax} text={count(item.shown)} />,
              clicks: count(item.clicks),
              cart: count(item.add_to_cart),
            }))}
          />
        </Section>
      )}
      {curated.items.length > 0 && (
        <Section
          title={translate("analyticsTopCurated")}
          note={topCurated.shown === 0 ? null : translate(topCurated.shown === 1 ? "analyticsTopCuratedLeadOne" : "analyticsTopCuratedLead", { title: topCurated.title || topCurated.id, count: count(topCurated.shown) })}
          info={products.items.length > 0 ? undefined : shownNote}
          className="space-y-2"
        >
          {products.items.length === 0 && clicksNoteView}
          <CardsTable
            caption={translate("analyticsTopCurated")}
            language={language}
            columns={[
              { key: "card", label: translate("analyticsCuratedCard") },
              ...(hasScope ? [{ key: "scope", label: translate("analyticsScope") }] : []),
              numeric("shown", translate("analyticsShownColumn")),
              numeric("clicks", translate("analyticsClicksColumn")),
            ]}
            rows={curated.items.map((item) => ({
              key: item.id,
              card: (
                <CardName title={item.title || item.id} media={{ url: item.media_url, type: item.media_type }} link={item.link_url}>
                  {item.configured && item.shown === 0 && item.clicks === 0 && (
                    <span className="text-[11px] px-1.5 py-px rounded" style={{ backgroundColor: "var(--color-delta-bad-bg)", color: "var(--color-delta-bad-text)" }}>
                      {translate("analyticsNeverShown")}
                    </span>
                  )}
                  {!item.configured && <span style={secondary}>({translate("analyticsCardNoLongerConfigured")})</span>}
                </CardName>
              ),
              scope: item.scope ?? "",
              shown: <InlineBar value={item.shown} max={curatedMax} text={count(item.shown)} />,
              clicks: count(item.clicks),
            }))}
          />
        </Section>
      )}
    </>
  );
};

export default CardsShown;
